import {defineStore} from 'pinia'
import type {Theme} from "~/types/theme";
import type {Continent, WorldDiagnostic} from "~/types/world";
import {reportStore} from "~/composables/reportStore";
import {PerimeterType} from "~/types/PerimeterType";

export const useWorldStore = defineStore('worldStore', () => {
    const runtimeConfig = useRuntimeConfig()
    const decathStore = useDecathlonStores()
    const worldDiagnostic = ref<WorldDiagnostic | undefined>()
    const useReportStore = reportStore()

    const getThemes = () => useReportStore.getThemes(worldDiagnostic.value?.diagnostics)

    const getThemeLabels = () => useReportStore.getThemeLabels(worldDiagnostic.value?.diagnostics)

    const getItemsForThemes = () => useReportStore.getItemsForThemes(worldDiagnostic.value?.diagnostics)

    const getItemsForSubThemes = (selectedTheme: Theme) => useReportStore.getItemsForSubThemes(worldDiagnostic.value?.diagnostics, selectedTheme)
    const getHeaderStats = () => {
        if (worldDiagnostic.value)
            return useReportStore.getHeaderStats(
                worldDiagnostic.value?.label,
                worldDiagnostic.value?.diagnostics[0].perimeter.activePerimeter,
                worldDiagnostic.value?.diagnostics[0].perimeter.perimeterSize,
                worldDiagnostic.value.rates, PerimeterType.COUNTRY)
        else
            return {
                name: '_',
                sizePerimeter: 0,
                rates: {
                    completion: 0,
                    satisfaction: 0
                },
                perimeterType: PerimeterType.COUNTRY
            }

    }

    const fetchContinents = async () => {

        // Start of base URL
        let url = `${runtimeConfig.public.apiBase}/v1/diagnostic/world/continents`;

        // Use the constructed URL
        return await useFetchWithToken<Continent[]>('fetchContinents', url, {
            method: 'get',
        })
    }

    const fetchWorldStoreExport = async () => {
        const params = new URLSearchParams()
        if (decathStore.selectedStore?.id) {
            params.append('storeId', decathStore.selectedStore.id.toString())
        }
        if (decathStore.selectedCrossDiag?.id) {
            params.append('diagnosticId', decathStore.selectedCrossDiag.id.toString())
        }
        const url = `${runtimeConfig.public.apiBase}/v1/diagnostic/world/export?${params.toString()}`

        const blob = await useFetchWithToken<Blob>('fetchWorldStoreExport', url, {
            method: 'GET',
            responseType: 'blob',
        })

        if (!blob) return false

        const label = (decathStore.selectedCrossDiag?.label ?? 'world')
            .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-zA-Z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
            .toLowerCase()
        const date = new Date().toISOString().slice(0, 10)
        const filename = `${label}_${date}.csv`

        const downloadUrl = URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = downloadUrl
        link.download = filename
        document.body.appendChild(link)
        link.click()
        link.remove()
        URL.revokeObjectURL(downloadUrl)
        return true
    }

    const fetchWorldDiagnostic = async (continentIds?: string[], diagnosticsStarted?: boolean) => {
        const baseUrl = `${runtimeConfig.public.apiBase}/v1/diagnostic/world`

        // keep the other params in query
        const params = new URLSearchParams()
        if (decathStore.selectedStore?.id) {
            params.append('storeId', decathStore.selectedStore.id.toString())
        }
        if (decathStore.selectedCrossDiag?.id) {
            params.append('diagnosticId', decathStore.selectedCrossDiag.id.toString())
        }

        params.append('aggregateLevel', 'COUNTRY')
        const url = `${baseUrl}?${params.toString()}`

        worldDiagnostic.value = undefined

        worldDiagnostic.value = await useFetchWithToken<WorldDiagnostic>('fetchWorldDiagnostic', url, {
            method: 'post',
            dedupe: 'cancel',
            body: {
                continentIds: continentIds?.length ? continentIds : [],
                diagnosticsStarted: diagnosticsStarted ?? true
            }
        })

        return worldDiagnostic.value
    }

    return {
        worldDiagnostic,
        fetchWorldDiagnostic,
        fetchWorldStoreExport,
        getHeaderStats,
        getItemsForThemes,
        getItemsForSubThemes,
        getThemeLabels,
        getThemes,
        fetchContinents
    }
})
